import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const avatarSrc = (a) =>
  a?.avatar_url
    ? (a.avatar_url.startsWith("/api/") ? `${API_BASE}${a.avatar_url}` : a.avatar_url)
    : `https://ui-avatars.com/api/?background=e5e5e5&color=0a0a0a&name=${encodeURIComponent(a?.name || "?")}`;

/** Square profile photo with upload (PNG/JPG/WEBP, 5MB) stored in object storage. */
export default function AthletePhoto({ athlete, size = "w-16 h-16", canEdit = true, onChange }) {
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(0);
  const ref = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be 5MB or smaller");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/athletes/${athlete.athlete_id}/photo`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      setBust(Date.now());
      onChange?.(data.avatar_url);
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed · please try again.");
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div className={`relative group ${size} shrink-0 mr-1`} data-testid="athlete-photo">
      <img src={`${avatarSrc(athlete)}${bust ? `${avatarSrc(athlete).includes("?") ? "&" : "?"}v=${bust}` : ""}`}
        alt={athlete?.name || ""} className={`${size} object-cover border border-border grayscale`} />
      {canEdit && (
        <>
          <button data-testid="athlete-photo-upload" aria-label="Change photo" onClick={() => ref.current?.click()} disabled={busy}
            className="absolute -bottom-1 -right-1 w-5 h-5 bg-background text-foreground border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-surface transition-opacity disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
          </button>
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} className="hidden" data-testid="athlete-photo-input" />
        </>
      )}
    </div>
  );
}
